import React, { useState } from 'react';
import { LANGUAGE_CODES } from '@techchange/i18n';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import {
	Button,
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
	Form,
	FormGroup,
	Input,
	Label,
	Modal,
	ModalBody,
} from 'reactstrap';

import { DragPreview, DragSource, DropTarget, DRAG_TYPES } from '@techchange/dnd';
import {
	DangerAlert,
	IconButton,
	API_BASE_URL,
	appPropTypes,
	useLanguageContext,
	getLanguageOptions,
} from '../../../_shared';
import { messages } from '../../../_shared/i18n/messages';

import { FIELD_TYPES } from '../../constants';
import { getWidthOptions, formShape } from '../../utils';
import FormField from '../FormField';

import FormEditorDragPreview from './components/FormEditorDragPreview';
import FormEditorField from './components/FormEditorField';
import FormEditorPreview from './components/FormEditorPreview';
import FormEditorSection from './components/FormEditorSection';
import { FormEditorProvider } from './contexts/FormEditorContext';
import {
	useFormEditorData,
	FORM_EDITOR_FIELDS,
	SECTION_EDITOR_FIELDS,
} from './hooks/useFormEditorData';
import { hasTranslationContent } from './utils/translations';

import './index.scss';

// Create a list of all the draggable types for the form editor
const DRAGGABLE_TYPES_FOR_FORM = [
	DRAG_TYPES.FORM_FIELD,
	DRAG_TYPES.FORM_FIELD_OPTION,
	DRAG_TYPES.FORM_SECTION,
];

const languageOptions = getLanguageOptions();

const axiosClient = axios.create({
	withCredentials: true,
});

const propTypes = {

	/**
	 * The form to edit.
	 */
	form: formShape.isRequired,

	/**
	 * The list of advanced navigation settings for the form, if any.
	 */
	advancedSettings: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The form section advanced settings ID.
		 */
		id: PropTypes.string.isRequired,

	})),

	/**
	 * The list of all the tickets associated with the product.
	 */
	tickets: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The ticket ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The ticket name.
		 */
		title: PropTypes.string.isRequired,

	})),

	/**
	 * The list of all the ticket groups associated with the product.
	 */
	ticketGroups: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The ticket group ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The ticket name.
		 */
		title: PropTypes.string.isRequired,

	})),

	/**
	 * The name of the organization (required for forms with privacy level in-use).
	 */
	orgName: PropTypes.string,

	/**
	 * True, if the privacy level is in-use for the form.
	 */
	showPrivacy: PropTypes.bool,

	/**
	 * Flag specifying if mobile view should be rendered.
	 */
	mobileView: PropTypes.bool,

	/**
	 * The settings for filestack to handle image uploads and rendering.
	 */
	filestack: appPropTypes.filestack,

	/**
	 * Callback function to handle cancelling the form editor.
	 */
	onCancel: PropTypes.func,

	/**
	 * Callback function to handle saving the form editor.
	 */
	onSave: PropTypes.func.isRequired,

	/**
	 * Callback function to handle changing the width of the form.
	 */
	onWidthChange: PropTypes.func,

};

const defaultProps = {
	advancedSettings: [],
};

/**
 * Shared component to render the form editor.
 *
 * @param {Object} props The props.
 * @returns {Object} The rendered component.
 */
const FormEditor = (props) => {

	const {
		form,
		tickets,
		ticketGroups,
		orgName,
		showPrivacy,
		mobileView,
		filestack,
		onCancel,
		onSave,
		onWidthChange,
	} = props;

	const intl = useIntl();

	const { preferredLanguage, defaultLanguage } = useLanguageContext();

	const WIDTH_OPTIONS = getWidthOptions(intl);

	const {
		formData,
		inputValidation,
		actions,
	} = useFormEditorData({
		form,
		tickets,
		ticketGroups,
		defaultLanguage,
		preferredLanguage,
		onLanguageChange: (id, language) => {

			return axiosClient.get(`${API_BASE_URL.FORMS}/${id}`, {
				params: {
					language,
					scope: form.scope,
					scope_id: form.scope_id,
				}
			}).then((updatedForm) => {

				return updatedForm;

			});

		},
		onSave,
		onWidthChange,
	});

	const [showPreviewModal, setShowPreviewModal] = useState(false);

	const [showTranslationDropdown, setShowTranslationDropdown] = useState(false);
	const [showError, setShowError] = useState(false);

	/**
	 * Callback to handle language change. Updates
	 * error state if error occurs.
	 *
	 * @param {String} fieldName  The name of the updated field.
	 * @param {String} fieldValue  The value of the updated field.
	 */
	const handleOnLanguageChange = async (fieldName, fieldValue) => {

		setShowError(false);

		try {

			await actions.updateLanguage(fieldName, fieldValue);

		} catch (error) {

			console.error(error);
			setShowError(true);

		}

	};

	/**
	 * Callback to handle form submission. Updates
	 * error state if error occurs.
	 *
	 * @param {Object} event  The JavaScript event object.
	 */
	const handleOnSubmit = async (event) => {

		event.preventDefault();

		try {

			setShowError(false);

			await actions.submitForm();

		} catch (error) {

			setShowError(true);

		}

	};

	// Check to see if a translation exists for the selected language
	const translationExists = hasTranslationContent(formData, formData[FORM_EDITOR_FIELDS.LANGUAGE]);

	// Check if allowed to delete the translations for the selected language
	const showDeleteButton =
		translationExists &&
		formData[FORM_EDITOR_FIELDS.LANGUAGE] !== defaultLanguage;

	// Check if editing content in default language for the form
	const isEditingDefaultLanguage = defaultLanguage === formData[FORM_EDITOR_FIELDS.LANGUAGE];

	return (
		<FormEditorProvider
			formData={formData}
			defaultLanguage={defaultLanguage}
			orgName={orgName}
			showPrivacy={showPrivacy}
			mobileView={mobileView}
			filestack={filestack}
			actions={actions}>
			<Form
				className="tc-form-editor"
				onSubmit={handleOnSubmit}>
				<DragPreview accept={DRAGGABLE_TYPES_FOR_FORM}>
					<FormEditorDragPreview />
				</DragPreview>
				{showError &&
					<div className="tc-form-editor-error-group">
						<DangerAlert>
							<FormattedMessage {...messages.TC_COMPONENTS_FORM_EDITOR_MSG_TEXT_SAVE_ERROR} />
						</DangerAlert>
					</div>
				}
				<div className="form-translation">
					<div className="form-translation-header">
						<FormField
							id="form-translation-language-field"
							className="form-translation-language-select me-auto"
							type={FIELD_TYPES.SELECT}
							name={FORM_EDITOR_FIELDS.LANGUAGE}
							value={formData[FORM_EDITOR_FIELDS.LANGUAGE]}
							options={languageOptions}
							label={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_LABEL_TEXT_FORM_LANGUAGE)}
							isEditing
							isLabelHidden
							onChange={handleOnLanguageChange} />
						<>
							<IconButton
								icon="far fa-eye fa-lg"
								size="40px"
								title={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_BUTTON_TEXT_PREVIEW, {
									language: LANGUAGE_CODES[formData[FORM_EDITOR_FIELDS.LANGUAGE]].name,
								})}
								onClick={() => setShowPreviewModal(true)} />
							<Modal
								size="lg"
								isOpen={showPreviewModal}
								toggle={() => setShowPreviewModal((prev) => !prev)}>
								<ModalBody>
									<FormEditorPreview
										tickets={tickets}
										ticketGroups={ticketGroups}
										onPreview={actions.generateFormPreview} />
								</ModalBody>
							</Modal>
						</>
						{showDeleteButton &&
							<Dropdown
								isOpen={showTranslationDropdown}
								toggle={() => setShowTranslationDropdown((prev) => !prev)}>
								<DropdownToggle
									tag={IconButton}
									id="form-translation-dropdown-toggle"
									title={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_MORE_LABEL)}
									size="2.1rem"
									placement="top"
									icon="fas fa-ellipsis-h"
									tooltip />
								<DropdownMenu end>
									<DropdownItem
										className="form-translation-dropdown-item"
										onClick={actions.deleteTranslation}>
										<span className="text-danger">
											{intl.formatMessage(messages.TC_COMPONENTS_GENERAL_DELETE_LABEL)}
										</span>
									</DropdownItem>
								</DropdownMenu>
							</Dropdown>
						}
					</div>
					<div className="form-translation-body">
						{translationExists &&
							<>
								{formData[FORM_EDITOR_FIELDS.SECTIONS]?.map((section, index) => (
									<DropTarget
										key={section.id}
										direction="vertical"
										type={DRAG_TYPES.FORM_SECTION}
										item={section}
										prevItem={formData[FORM_EDITOR_FIELDS.SECTIONS][index - 1]}
										nextItem={formData[FORM_EDITOR_FIELDS.SECTIONS][index + 1]}>
										<DragSource
											type={DRAG_TYPES.FORM_SECTION}
											item={section}
											onMove={actions.sections.move}>
											<FormEditorSection
												section={section}
												sectionIndex={index}
												sectionValidation={
													inputValidation?.[FORM_EDITOR_FIELDS.SECTIONS]?.data?.[section.id]
												}
												showAdvancedOption={Boolean(tickets && ticketGroups)}>
												{section[SECTION_EDITOR_FIELDS.FIELDS]?.map((field, fieldIndex) => {
													return (
														<DropTarget
															key={field.id}
															direction="vertical"
															type={DRAG_TYPES.FORM_FIELD}
															item={field}
															prevItem={section[SECTION_EDITOR_FIELDS.FIELDS][fieldIndex - 1]}
															nextItem={section[SECTION_EDITOR_FIELDS.FIELDS][fieldIndex + 1]}>
															<DragSource
																type={DRAG_TYPES.FORM_FIELD}
																item={field}
																onMove={actions.fields.move}>
																<FormEditorField
																	key={field.id}
																	field={field}
																	fieldIndex={fieldIndex}
																	fieldValidation={
																		// eslint-disable-next-line max-len
																		inputValidation?.[FORM_EDITOR_FIELDS.SECTIONS]?.data?.[section.id]?.[SECTION_EDITOR_FIELDS.FIELDS]?.data?.[field.id]
																	} />
															</DragSource>
														</DropTarget>
													);
												})}
											</FormEditorSection>
										</DragSource>
									</DropTarget>
								))}
								{isEditingDefaultLanguage &&
									<div className="tc-form-editor-question-dropdown-container">
										<Button
											className="new-section-btn"
											color="neutral"
											outline
											onClick={actions.sections.add}>
											<i
												className="fal fa-circle-plus"
												aria-hidden="true" />
											{' '}
											<FormattedMessage
												{...messages.TC_COMPONENTS_FORM_EDITOR_BUTTON_TEXT_ADD_NEW_SECTION} />
										</Button>
									</div>
								}
							</>
						}
						{!translationExists &&
							<div className="missing-translation-msg">
								<p className="text-center">
									<FormattedMessage
										{...messages.TC_COMPONENTS_FORM_EDITOR_HELPER_TEXT_ADD_TRANSLATION}
										values={{
											language: LANGUAGE_CODES[formData[FORM_EDITOR_FIELDS.LANGUAGE]].name,
										}} />
								</p>
								<Button
									color="neutral"
									outline
									onClick={actions.addTranslation}>
									<FormattedMessage
										{...messages.TC_COMPONENTS_FORM_EDITOR_BUTTON_LABEL_TEXT_TRANSLATION_ADD} />
								</Button>
							</div>
						}
					</div>
				</div>
				{isEditingDefaultLanguage &&
					formData[FORM_EDITOR_FIELDS.WIDTH] &&
					onWidthChange &&
						<FormGroup className="tc-form-group-width">
							<Label for="width">
								<FormattedMessage {...messages.TC_COMPONENTS_FORM_EDITOR_LABEL_TEXT_FORM_WIDTH} />
							</Label>
							<Input
								type="select"
								name={FORM_EDITOR_FIELDS.WIDTH}
								value={formData[FORM_EDITOR_FIELDS.WIDTH]}
								onChange={
									(event) => actions.updateForm(FORM_EDITOR_FIELDS.WIDTH, event.target.value)
								}>
								{WIDTH_OPTIONS.map((option) => (
									<option
										key={option.val}
										value={option.val}>
										{option.label}
									</option>
								))}
							</Input>
						</FormGroup>
				}
				<FormGroup>
					<Button
						color="primary"
						type="submit">
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_SAVE_LABEL} />
					</Button>
					{onCancel &&
						<Button
							className="ms-3"
							color="neutral"
							type="button"
							outline
							onClick={onCancel}>
							<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_CANCEL_LABEL} />
						</Button>
					}
				</FormGroup>
			</Form>
		</FormEditorProvider>
	);

};

FormEditor.defaultProps = defaultProps;
FormEditor.propTypes = propTypes;

export default FormEditor;
